/**
 * Practice-mode setup screen (#499 — piece 3/3; restructured #676 into a
 * 3v3-only screen — see the batch issue's decision log). Reachable from
 * `HomeScreen`'s entry point, which is now shown to every user (pre-release:
 * no client-side gating yet). The REAL enforcement is server-side
 * (`isTesterFlagged()`, `apps/server/src/ws.ts`) — this screen existing at
 * all is presentation, not security.
 *
 * Pick your own champion/loadout (the same `ChampionPicker`/`LoadoutPicker`
 * every other setup screen uses), then an explicit TWO-COLUMN "your team" /
 * "opponent team" grouping — each seat independently human (open,
 * share-link) or a specific bot checkpoint (`OpponentSeatPicker`, sourced
 * from a live `listBots` reply). Mirrors #662's training-dashboard
 * own-team/opponent-team roster-picker convention.
 *
 * ISSUE-773 (#676's promised fast-follow — the pre-#676 1v1-vs-bot/human
 * screen, restored as a MODE rather than a third screen): the seat counts
 * are a parameter of the selected team size, not the two hardcoded
 * constants #676 left behind. 1v1 sends `teammateSeats: []` +
 * `opponentSeats: [<one seat>]`, which `ws.ts` has always accepted
 * (`teamSize = teammateSeats.length + 1`); 3v3 is byte-identical to #676's
 * behavior. Only 1 and 3 are selectable — the server hard-rejects any other
 * team size (`PracticeTeamSize`). In 1v1 the "your team" column has no
 * seats at all, so it is omitted rather than rendered empty.
 *
 * ISSUE-773 also widened `ws.ts`'s tester-flag carve-out from "dummy-only
 * 1v1" to "any solo (`teamSize === 1`) practice request", so this screen's
 * 1v1 mode is reachable by every player, exactly like `DummyPracticeScreen`
 * (#609). 3v3 stays tester-gated server-side. Solo-vs-a-stationary-DUMMY
 * practice remains its own separate screen (#609) — untouched here.
 *
 * Saveable/replayable: every successful create SAVES the resolved config
 * (`prefs.ts`'s `savePracticeConfig`) — including, for any bot seat, the
 * PINNED `manifestId` (not champion+tier) — so revisiting this screen (or a
 * future "replay this config" action) reproduces the exact same match
 * setup, unaffected by the manifest gaining newer entries later.
 *
 * #678: also carries a `MapPicker` (seat-0-only `mapSlug` override) — this
 * screen's `createMatch` call is always seat 0 (the creator), so the
 * picker's choice always applies.
 */

import { useEffect, useRef, useState, type ReactElement } from "react";
import type { OpponentSeat } from "@pit/protocol";
import { useWsClient } from "../net/WsClientProvider.js";
import { useStore } from "../store/context.js";
import { navigate } from "../router/hashRoute.js";
import { ChampionLoadoutPicker } from "../champions/ChampionLoadoutPicker.js";
import { useChampionLoadoutState } from "../champions/useChampionLoadoutState.js";
import { loadPracticeConfig, savePracticeConfig } from "../prefs.js";
import { MapPicker } from "./MapPicker.js";
import { LIVE_MAP_SLUG } from "../render/arenaCatalog.js";
import {
  DEFAULT_OPPONENT_SEAT_DRAFT,
  OpponentSeatPicker,
  opponentSeatDraftReady,
  opponentSeatToDraft,
  resolveOpponentSeatDraft,
  type OpponentSeatDraft,
} from "./OpponentSeatPicker.js";
import {
  PRACTICE_TEAM_SIZES,
  resolveSavedChampionId,
  resolveSavedLoadout,
  resolveSavedOpponentSeats,
  resolveSavedPracticeBotCount,
  resolveSavedTeamSize,
  PRACTICE_BOT_COUNT_MAX,
  PRACTICE_BOT_COUNT_MIN,
  resolveSavedTeammateSeats,
  teammateSeatCount,
  type PracticeTeamSize,
} from "./resolvePracticeConfig.js";

/** Grow/shrink a draft list to exactly `count` entries, padding with the
 *  human default. Used both to seed from a saved config and to re-fit the
 *  seats when the team size changes (ISSUE-773) — a kept prefix means
 *  toggling 3v3 → 1v1 → 3v3 doesn't lose the first seat's bot pick. */
function resizeDrafts(drafts: readonly OpponentSeatDraft[], count: number): OpponentSeatDraft[] {
  const out: OpponentSeatDraft[] = [];
  for (let i = 0; i < count; i++) {
    out.push(drafts[i] ?? DEFAULT_OPPONENT_SEAT_DRAFT);
  }
  return out;
}

function toDrafts(seats: readonly OpponentSeat[], count: number): OpponentSeatDraft[] {
  return resizeDrafts(seats.map(opponentSeatToDraft), count);
}

function replaceAt<T>(arr: readonly T[], index: number, value: T): T[] {
  const next = [...arr];
  next[index] = value;
  return next;
}

/** Resolve every draft to a real `OpponentSeat`, or `null` if any bot-kind
 *  draft has no pick yet (mirrors `opponentSeatDraftReady`'s own check). */
function resolveDrafts(drafts: readonly OpponentSeatDraft[]): readonly OpponentSeat[] | null {
  const out: OpponentSeat[] = [];
  for (const d of drafts) {
    const resolved = resolveOpponentSeatDraft(d);
    if (resolved === null) return null;
    out.push(resolved);
  }
  return out;
}

export function PracticeSetupScreen(): ReactElement {
  const client = useWsClient();
  const { state, dispatch } = useStore();
  const requestedBots = useRef(false);
  const [pending, setPending] = useState(false);

  const saved = loadPracticeConfig();
  const initialChampionId = resolveSavedChampionId(saved);
  const initialLoadout = resolveSavedLoadout(saved, initialChampionId);

  const loadoutState = useChampionLoadoutState({
    initialChampionId,
    initialLoadout,
  });
  const { championId, loadout, isValid: loadoutValid } = loadoutState;

  // ISSUE-773: the selected team size drives every seat count below.
  const [teamSize, setTeamSize] = useState<PracticeTeamSize>(() => resolveSavedTeamSize(saved));
  const [practiceBotCount, setPracticeBotCount] = useState<number | null>(() =>
    resolveSavedPracticeBotCount(saved),
  );
  const [teammates, setTeammates] = useState<readonly OpponentSeatDraft[]>(() =>
    resolveSavedPracticeBotCount(saved) === null
      ? toDrafts(resolveSavedTeammateSeats(saved), teammateSeatCount(resolveSavedTeamSize(saved)))
      : [{ kind: "human", manifestId: null }],
  );
  const [opponents, setOpponents] = useState<readonly OpponentSeatDraft[]>(() =>
    toDrafts(
      resolveSavedOpponentSeats(saved),
      resolveSavedPracticeBotCount(saved) ?? resolveSavedTeamSize(saved),
    ),
  );
  // #678: seat-0-only map override — this screen's creator is always seat 0.
  const [mapSlug, setMapSlug] = useState(LIVE_MAP_SLUG);

  const canConnect = state.connection.kind === "welcomed";

  // Request the bot roster once, as soon as we're welcomed.
  useEffect(() => {
    if (!canConnect) return;
    if (requestedBots.current) return;
    requestedBots.current = true;
    client.listBots();
  }, [canConnect, client]);

  // Same create → hop-to-Match flow as HomeScreen's "Create private match".
  useEffect(() => {
    if (pending && state.matchId !== null && state.joinUrl !== null) {
      navigate({ kind: "match" });
      setPending(false);
    }
  }, [pending, state.matchId, state.joinUrl]);

  useEffect(() => {
    if (pending && state.serverError !== null) {
      setPending(false);
    }
  }, [pending, state.serverError]);

  const seatsReady =
    teammates.every(opponentSeatDraftReady) && opponents.every(opponentSeatDraftReady);
  const canCreate = canConnect && loadoutValid && seatsReady;
  const modeLabel =
    practiceBotCount === null
      ? String(teamSize) + "v" + String(teamSize)
      : "2v" + String(practiceBotCount);

  /** ISSUE-773: switching mode re-fits BOTH seat lists to the new size —
   *  the seat plan and the selected team size can never disagree, which is
   *  exactly what `ws.ts` validates (`opponentSeats.length === teamSize`). */
  function changeTeamSize(next: PracticeTeamSize): void {
    setPracticeBotCount(null);
    setTeamSize(next);
    setTeammates(resizeDrafts(teammates, teammateSeatCount(next)));
    setOpponents(resizeDrafts(opponents, next));
  }

  function changePracticeBotCount(next: number): void {
    setPracticeBotCount(next);
    setTeammates([{ kind: "human", manifestId: null }]);
    setOpponents(Array.from({ length: next }, () => ({ kind: "bot", manifestId: null })));
  }

  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        width: "100%",
        height: "100%",
        flexDirection: "column",
        gap: 16,
        padding: 32,
      }}
    >
      <h1 style={{ margin: 0 }}>Practice mode ({modeLabel})</h1>
      <p style={{ opacity: 0.7, fontSize: 13, maxWidth: 560, textAlign: "center" }}>
        {practiceBotCount !== null
          ? "Invite one teammate, then fight " +
            String(practiceBotCount) +
            " bots. Bots retain their existing 3v3 awareness, so this is an intentionally less-coordinated swarm challenge."
          : teamSize === 1
            ? "Set up a 1v1 practice match: pick your champion, then choose your single opponent — either another human (share link) or a specific bot checkpoint."
            : "Set up a 3v3 practice match: pick your champion, then fill your team's other 2 seats and the opposing team's 3 seats — each is either another human (share link) or a specific bot checkpoint."}
      </p>
      <fieldset
        data-testid="practice-mode"
        style={{
          border: "1px solid #3a4757",
          borderRadius: 6,
          padding: 10,
          display: "flex",
          gap: 12,
        }}
      >
        <legend style={{ opacity: 0.75, fontSize: 13 }}>Team size</legend>
        {PRACTICE_TEAM_SIZES.map((size) => (
          <label key={size} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 14 }}>
            <input
              type="radio"
              name="practice-team-size"
              data-testid={`practice-mode-${String(size)}v${String(size)}`}
              checked={teamSize === size}
              disabled={!canConnect}
              onChange={() => {
                changeTeamSize(size);
              }}
            />
            {`${String(size)}v${String(size)}`}
          </label>
        ))}
      </fieldset>
      <fieldset
        data-testid="practice-bot-count"
        style={{ border: "1px solid #3a4757", borderRadius: 6, padding: 10 }}
      >
        <legend style={{ opacity: 0.75, fontSize: 13 }}>Co-op swarm bots</legend>
        <select
          data-testid="practice-bot-count-select"
          value={practiceBotCount ?? ""}
          disabled={!canConnect}
          onChange={(event) => {
            const value = event.target.value;
            if (value === "") setPracticeBotCount(null);
            else changePracticeBotCount(Number(value));
          }}
        >
          <option value="">Off (symmetric mode)</option>
          {Array.from(
            { length: PRACTICE_BOT_COUNT_MAX },
            (_, index) => index + PRACTICE_BOT_COUNT_MIN,
          ).map((count) => (
            <option key={count} value={count}>
              {String(count) + " bots (2v" + String(count) + ")"}
            </option>
          ))}
        </select>
      </fieldset>
      <ChampionLoadoutPicker
        state={loadoutState}
        disabled={!canConnect}
        layoutStyle={{ alignItems: "center" }}
      />
      <MapPicker value={mapSlug} onChange={setMapSlug} disabled={!canConnect} />
      <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>
        {/* ISSUE-773: 1v1 has no teammate seats at all — omit the column
            rather than render an empty "Your team" heading. */}
        {teammates.length > 0 ? (
          <div
            data-testid="practice-your-team"
            style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 220 }}
          >
            <h2 style={{ margin: 0, fontSize: 15 }}>Your team</h2>
            {teammates.map((draft, i) => (
              <OpponentSeatPicker
                key={i}
                label={`Teammate ${String(i + 1)}`}
                value={draft}
                onChange={(next) => {
                  setTeammates(replaceAt(teammates, i, next));
                }}
                bots={state.practiceBots}
                disabled={!canConnect}
                testIdPrefix={`teammate-${String(i)}`}
              />
            ))}
          </div>
        ) : null}
        <div
          data-testid="practice-opponent-team"
          style={{ display: "flex", flexDirection: "column", gap: 8, minWidth: 220 }}
        >
          <h2 style={{ margin: 0, fontSize: 15 }}>
            {teamSize === 1 ? "Opponent" : "Opponent team"}
          </h2>
          {opponents.map((draft, i) => (
            <OpponentSeatPicker
              key={i}
              label={teamSize === 1 ? "Opponent" : `Opponent ${String(i + 1)}`}
              value={draft}
              onChange={(next) => {
                setOpponents(replaceAt(opponents, i, next));
              }}
              bots={state.practiceBots}
              disabled={!canConnect}
              testIdPrefix={`opponent-${String(i)}`}
            />
          ))}
        </div>
      </div>
      <button
        type="button"
        data-testid="practice-create"
        disabled={!canCreate}
        onClick={() => {
          const teammateSeats = resolveDrafts(teammates);
          const opponentSeats = resolveDrafts(opponents);
          if (teammateSeats === null || opponentSeats === null) return;
          if (state.matchId !== null) dispatch({ type: "match.leave" });
          dispatch({ type: "match.champion", championId, loadout });
          client.createMatch(championId, [...loadout], {
            teammateSeats,
            opponentSeats,
            ...(practiceBotCount !== null ? { practiceBotCount } : {}),
            mapSlug,
          });
          savePracticeConfig({
            championId,
            loadout: [...loadout],
            teammateSeats: [...teammateSeats],
            opponentSeats: [...opponentSeats],
            ...(practiceBotCount !== null ? { practiceBotCount } : {}),
          });
          setPending(true);
        }}
        style={{
          padding: "10px 24px",
          fontSize: 15,
          background: canCreate ? "#3aa675" : "#333",
          color: "#fff",
          border: "none",
          borderRadius: 6,
        }}
      >
        Create practice match
      </button>
      <button
        type="button"
        data-testid="practice-back"
        onClick={() => {
          navigate({ kind: "home" });
        }}
        style={{
          padding: "8px 16px",
          background: "transparent",
          color: "#dfe6ef",
          border: "1px solid #3a4757",
          borderRadius: 6,
        }}
      >
        Back
      </button>
      {!canConnect ? <div style={{ opacity: 0.6 }}>connecting…</div> : null}
    </div>
  );
}
